import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Stack,
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { isEmpty } from 'lodash';

import { AppContext } from 'views/App/context';
import { translate as t } from 'hooks/useTranslations';
import { useQueryClient } from 'react-query';
import useManageManual from '../_data_sources/useManageManual';
import useVerticals from 'data_sources/useVerticals';
import useManualTypes from 'data_sources/useManualTypes';
import { Form } from 'components/_forms';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import { enqueueSnackbar } from 'notistack';
import { neutral } from '_lex/_foundations/phxColors';

const CreateGlobalManual: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganizationId: orgId, isLexipolStaff } = useContext(AppContext);

  const [verticalId, setVerticalId] = useState('');
  const [manualTypeId, setManualTypeId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();

  const queryClient = useQueryClient();
  const { createGlobalManual } = useManageManual();
  const verticalsQuery = useVerticals();
  const manualTypesQuery = useManualTypes();

  // Generate title based on selections
  const generatedTitle = useMemo(() => {
    if (!verticalId || !manualTypeId) return '';

    const vertical = verticalsQuery.data?.find((v) => v.id === verticalId);
    const verticalName = vertical?.name || verticalId;

    const manualType = manualTypesQuery.data?.find((mt) => mt.id === manualTypeId);
    const manualTypeName = manualType?.name || manualTypeId;

    return `Global ${verticalName} ${manualTypeName} Manual`;
  }, [verticalId, manualTypeId, verticalsQuery.data, manualTypesQuery.data]);

  // Redirect if not Lexipol staff
  useEffect(() => {
    if (!isLexipolStaff) {
      navigate('..');
    }
  }, [isLexipolStaff, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verticalId || !manualTypeId) {
      return;
    }

    const payload = {
      manualLevel: 'GLOBAL' as const,
      manualTypeId,
      verticalId,
    };

    setIsCreating(true);
    setServerErrors(undefined);

    createGlobalManual(
      orgId || '',
      payload,
      (response) => {
        enqueueSnackbar(t('draftManuals.createGlobalManual.success', '', { manualName: generatedTitle }), {
          variant: 'success',
        });
        queryClient.invalidateQueries(['draft_manuals']);
        navigate('..');
      },
      (error) => {
        setServerErrors(error);
        setIsCreating(false);
      }
    );
  };

  // Return null if not Lexipol staff
  if (!isLexipolStaff) {
    return null;
  }

  return (
    <TakeoverLayout
      pageTitle={t('draftManuals.createGlobalManual.title')}
      backUrl='..'
    >
      <Box>
        <Card
          sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
          className='lex-form'
        >
          <Form onSubmit={handleSubmit}>
            <CardContent>
              {errorHelper.renderAllErrors(serverErrors)}
              <Stack spacing={4}>
                {/* Jurisdiction Field - Disabled and auto-filled */}
                <TextField
                  label={t('draftManuals.createGlobalManual.form.jurisdiction')}
                  value='Global'
                  disabled
                  fullWidth
                />

                {/* Vertical Field - Required dropdown */}
                <FormControl fullWidth>
                  <FormLabel>{t('draftManuals.createGlobalManual.form.vertical')}</FormLabel>
                  <Select
                    value={verticalId}
                    onChange={(e) => setVerticalId(e.target.value)}
                    displayEmpty
                    disabled={verticalsQuery.isLoading}
                  >
                    <MenuItem
                      value=''
                      disabled
                    >
                      {t('draftManuals.createGlobalManual.form.verticalPlaceholder')}
                    </MenuItem>
                    {verticalsQuery.data?.map((vertical) => (
                      <MenuItem
                        key={vertical.id}
                        value={vertical.id}
                      >
                        {vertical.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Manual Type Field - Radio buttons */}
                <FormControl>
                  <FormLabel>{t('draftManuals.createGlobalManual.form.manualType')}</FormLabel>
                  <RadioGroup
                    row
                    value={manualTypeId}
                    onChange={(e) => setManualTypeId(e.target.value)}
                  >
                    {manualTypesQuery.data?.map((manualType) => (
                      <FormControlLabel
                        key={manualType.id}
                        value={manualType.id}
                        control={<Radio />}
                        label={manualType.name}
                        disabled={manualTypesQuery.isLoading}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>

                {/* Title Field - Disabled and auto-generated */}
                <TextField
                  label={t('draftManuals.createGlobalManual.form.title')}
                  value={generatedTitle}
                  disabled
                  fullWidth
                  helperText={t('draftManuals.createGlobalManual.form.titleHelper')}
                />
              </Stack>
            </CardContent>
            <CardActions>
              <Box />
              <Stack
                direction='row'
                spacing={2}
              >
                <Button
                  color='neutral'
                  href='..'
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={isCreating || isEmpty(verticalId) || isEmpty(manualTypeId)}
                  component='button'
                  type='submit'
                  variant='contained'
                  endIcon={
                    isCreating ? (
                      <CircularProgress
                        size={20}
                        color='inherit'
                      />
                    ) : undefined
                  }
                >
                  {t('draftManuals.createGlobalManual.form.create')}
                </Button>
              </Stack>
            </CardActions>
          </Form>
        </Card>
      </Box>
    </TakeoverLayout>
  );
};

export default CreateGlobalManual;
